import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Clock, Loader2, TrendingUp, Users } from 'lucide-react';
import { toast } from 'sonner';
import { formatUtcHourLocal, utcHourToLocalHour, getLocalTzAbbr } from '../../lib/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom weekday × time-of-day bands defined in the viewer's LOCAL hours.
// The backend still returns a 7×24 UTC-hour grid — we fold it into local
// hours below via `utcHourToLocalHour`.
//
// Windows (per product request):
//   6 AM–10 AM  | 10 AM–2 PM | 2 PM–5 PM | 5 PM–9 PM | 9 PM–6 AM (overnight)
const HOUR_BANDS = [
  { label: '6 AM–10 AM',  localHours: [6, 7, 8, 9] },
  { label: '10 AM–2 PM',  localHours: [10, 11, 12, 13] },
  { label: '2 PM–5 PM',   localHours: [14, 15, 16] },
  { label: '5 PM–9 PM',   localHours: [17, 18, 19, 20] },
  { label: '9 PM–6 AM',   localHours: [21, 22, 23, 0, 1, 2, 3, 4, 5] },
];

const cellColor = (count, max) => {
  if (!count) return 'bg-slate-50 text-slate-300';
  const pct = max ? count / max : 0;
  if (pct >= 0.8) return 'bg-teal-600 text-white';
  if (pct >= 0.6) return 'bg-teal-500 text-white';
  if (pct >= 0.4) return 'bg-teal-400 text-white';
  if (pct >= 0.2) return 'bg-teal-200 text-teal-900';
  return 'bg-teal-50 text-teal-700';
};

const AgentSelector = ({ agents, agentId, onAgentChange }) => (
  <div className="flex items-center gap-1 mr-2">
    <Users className="w-3.5 h-3.5 text-slate-400" />
    <select
      value={agentId || ''}
      onChange={(e) => onAgentChange(e.target.value || null)}
      className="text-xs bg-white border border-slate-200 rounded-md px-2 py-1 text-slate-700 hover:border-teal-300 focus:outline-none focus:border-teal-400 max-w-[160px]"
      data-testid="heatmap-agent-select"
    >
      <option value="">All agents</option>
      {agents.map(a => (
        <option key={a.agent_id} value={a.agent_id}>
          {a.agent_name} (ext {a.agent_id})
        </option>
      ))}
    </select>
  </div>
);

const RangeButtons = ({ days, onDaysChange }) => (
  <>
    {[7, 30, 90].map((d) => (
      <button
        key={d}
        onClick={() => onDaysChange(d)}
        className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
          days === d
            ? 'bg-teal-500 text-white border-teal-500'
            : 'bg-white text-slate-500 border-slate-200 hover:border-teal-300'
        }`}
        data-testid={`heatmap-days-${d}`}
      >
        {d}d
      </button>
    ))}
  </>
);

const HeatmapCell = ({ count, max, weekdayIdx, band, weekdayLabel, onCellClick }) => {
  const clickable = count > 0 && !!onCellClick;
  const title = `${weekdayLabel} ${band.label} — ${count} call${count === 1 ? '' : 's'}${clickable ? ' · click to view' : ''}`;
  return (
    <td className="p-0.5">
      <button
        type="button"
        disabled={!clickable}
        onClick={() => clickable && onCellClick({
          weekday: weekdayIdx,
          localHours: band.localHours,
          label: `${weekdayLabel} ${band.label}`,
        })}
        className={`w-full rounded text-center py-2 text-xs font-semibold tabular-nums ${cellColor(count, max)} ${clickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : 'cursor-default'}`}
        title={title}
        data-testid={`heatmap-cell-${weekdayIdx}-${band.localHours[0]}`}
      >
        {count || '·'}
      </button>
    </td>
  );
};

export const CallVolumeHeatmap = ({ domainId = null, token = null, onCellClick = null, forcedAgentId = null, daysOverride = null }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [agentId, setAgentId] = useState(forcedAgentId);
  const [agents, setAgents] = useState([]);

  useEffect(() => { setAgentId(forcedAgentId); }, [forcedAgentId]);
  // Page-level date filter drives the 7/30/90 toggle (nearest preset) —
  // the buttons stay usable but re-sync whenever the page filter moves.
  useEffect(() => {
    if (daysOverride == null) return;
    const n = typeof daysOverride === 'number' ? daysOverride : 7;
    setDays(n <= 14 ? 7 : n <= 60 ? 30 : 90);
  }, [daysOverride]);

  // Load agents list for filter dropdown (domain-scoped)
  useEffect(() => {
    if (!domainId) return;
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    axios.get(`${API}/analytics/team?domain_id=${domainId}`, headers ? { headers } : {})
      .then(r => {
        const lb = r.data?.leaderboard || [];
        setAgents(lb.map(a => ({ agent_id: a.agent_id, agent_name: a.agent_name })));
      })
      .catch(() => { /* silent */ });
  }, [domainId, token]);

  useEffect(() => {
    const params = new URLSearchParams({ days: String(days) });
    if (domainId) params.set('domain_id', domainId);
    if (agentId) params.set('agent_id', agentId);
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    setLoading(true);
    axios.get(`${API}/analytics/volume-heatmap?${params}`, headers ? { headers } : {})
      .then(r => setData(r.data))
      .catch(err => {
        console.error('Volume heatmap load error:', err);
        toast.error('Failed to load call volume heatmap');
      })
      .finally(() => setLoading(false));
  }, [domainId, token, days, agentId]);

  // Remap the server-side UTC-hour grid (7 × 24) into a LOCAL-hour grid,
  // then fold it into the custom time-of-day bands. `utcHourToLocalHour`
  // returns today's local hour for a given UTC hour, so the day-shift is
  // implicit — a UTC Mon 22:00 call becomes local Tue 08:00 for a
  // Brisbane viewer (UTC+10).
  const folded = useMemo(() => {
    if (!data?.grid) return { rows: [], max: 0 };

    // Constant offset (hours) between local and UTC on today's date.
    // For Brisbane this is +10 (AEST) or +11 (AEDT after DST kicks in,
    // though AEST doesn't observe DST). Good enough for aggregating a
    // rolling 7/30/90-day window into weekday buckets.
    const offset = ((utcHourToLocalHour(0) ?? 0) + 24) % 24;

    // Build a 7×24 local grid: localGrid[localWeekday][localHour] = count.
    const localGrid = Array.from({ length: 7 }, () => Array(24).fill(0));
    for (let wd = 0; wd < 7; wd++) {
      for (let uh = 0; uh < 24; uh++) {
        const cnt = data.grid[wd]?.[uh] || 0;
        if (!cnt) continue;
        const total = uh + offset;
        const dayShift = Math.floor(total / 24);
        const lh = ((total % 24) + 24) % 24;
        const lwd = ((wd + dayShift) % 7 + 7) % 7;
        localGrid[lwd][lh] += cnt;
      }
    }

    const rows = localGrid.map((hourly) =>
      HOUR_BANDS.map((b) => b.localHours.reduce((sum, h) => sum + (hourly[h] || 0), 0))
    );
    const max = Math.max(...rows.flat(), 0);
    return { rows, max };
  }, [data]);

  if (loading) {
    return (
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-teal-500" />
        </CardContent>
      </Card>
    );
  }

  if (!data?.total_calls) {
    return (
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center gap-2 pb-3">
          <Clock className="w-5 h-5 text-teal-500" />
          <CardTitle className="text-base">Call Volume Heatmap</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-400 text-sm py-6 text-center">
            No calls in the selected window{agentId ? ' for this agent' : ''}.
          </p>
        </CardContent>
      </Card>
    );
  }

  const { weekdays, peak, total_calls: totalCalls } = data;
  const selectedAgent = agents.find(a => a.agent_id === agentId);

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="call-volume-heatmap">
      <CardHeader className="flex flex-row items-center gap-2 pb-3 flex-wrap">
        <Clock className="w-5 h-5 text-teal-500" />
        <CardTitle className="text-base">Call Volume Heatmap</CardTitle>
        <span className="text-xs text-slate-400 ml-1">
          by weekday × time-of-day · {getLocalTzAbbr()}
          {selectedAgent && <span className="text-teal-600 font-medium"> · {selectedAgent.agent_name}</span>}
        </span>
        <div className="ml-auto flex items-center gap-1 flex-wrap">
          {agents.length > 0 && (
            <AgentSelector agents={agents} agentId={agentId} onAgentChange={setAgentId} />
          )}
          <RangeButtons days={days} onDaysChange={setDays} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="text-xs text-slate-400 font-medium px-2 py-1.5 text-left w-14"> </th>
                {HOUR_BANDS.map((b) => (
                  <th key={b.label} className="text-xs text-slate-500 font-medium px-1 py-1.5 text-center">
                    {b.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {folded.rows.map((row, wdIdx) => (
                <tr key={weekdays[wdIdx]}>
                  <td className="text-xs text-slate-500 font-semibold px-2 py-1 text-left">
                    {weekdays[wdIdx]}
                  </td>
                  {row.map((count, hIdx) => (
                    <HeatmapCell
                      key={`${wdIdx}-${hIdx}`}
                      count={count}
                      max={folded.max}
                      weekdayIdx={wdIdx}
                      band={HOUR_BANDS[hIdx]}
                      weekdayLabel={weekdays[wdIdx]}
                      onCellClick={onCellClick}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5 text-teal-500" />
            <span>Peak: <strong className="text-slate-700">{peak.weekday} {formatUtcHourLocal(peak.hour)}</strong> ({peak.count} calls)</span>
          </div>
          <span>{totalCalls} calls analysed over {days} days{selectedAgent ? ` for ${selectedAgent.agent_name}` : ''}</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default CallVolumeHeatmap;
